import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { isNil } from 'lodash';
import { pathOr, not } from 'ramda';
import { Row, Col } from 'react-bootstrap';
import { FormattedMessage, injectIntl } from 'react-intl';
import moment from 'moment';
import styled from 'styled-components';
import {
  Box,
  Button,
  Stack,
  Typography,
  Tooltip,
  ListItemIcon,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AlertInfoIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';

import {
  useModal,
  Modal,
  MoveOutProrateBanner,
} from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { AlertCriticalIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { red } from '@fortress-technology-solutions/fortress-component-library/design';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { toastr } from 'react-redux-toastr';

import confirm from '../../../components/ConfirmDialogModal';
import XlsxIcon from '../../../components/Icons/xlsx';
import PdfIcon from '../../../components/Icons/pdf';
import ElementWithPermissions from '../../../components/ElementWithPermissions';
import {
  Divider,
  DetailList,
} from '../../../components/ProfileVisualComponents';
import FormattedDateOrDashes from '../../../components/FormattedDateOrDashes';
import {
  useAsyncDownloadFASDocument,
  useGetFinalAccountStatement,
  useGetFinalAccountStatementReversal,
} from './hooks';
import { useFetchPropertyConfig } from '../../App/hooks';
import IconTitle from '../../../components/ProfileVisualComponents/IconTitle';
import FinalAccountStatementReversalService from '../../../services/finalAccountStatementReversalService';
import StartTransferModal from '../StartTransferModal';
import { useStartTransferEligibility } from '../StartTransferModal/hooks';
import { useActiveTransfer } from '../ResidentTransferCard/hooks';
import {
  buildTransferActionLayout,
  TRANSFER_ACTION_ROW_SPACING,
} from '../transferActionLayout';
import useHasPermission from '../../../hooks/useHasPermission';
import { useHudRecertDueDate } from '../hooks';
import { isHudRecertOverdueForMoveOut } from '../utils';

import messages from './messages';

type Props = {
  isResident: boolean,
  monthsWithLateFees: number,
  doNotRenew: boolean,
  underEviction: boolean,
  users: Array<Object>,
  ledgerBalance: string,
  intl: Object,
  handleAssignedToChange: Function,
  onProcessMoveOutClick: Function,
  handleNoticeToVacateClick: Function,
  handleDoNotRenewClick: Function,
  fasReady: boolean,
  accountFinalized: boolean,
  manualWriteOff: Function,
  handleUnderEvictionClick: Function,
  handleRenewLease: Function,
  currentLease: Object,
  hasOpenTransfer: boolean,
  showEditMoveInDateLink: Function,
  /** "View Household History (N)" under the Property Move-In Date, when the household had several units. */
  unitHistoryLink?: React$Node,
  showEditMoveOutDateLink: Function,
  householdInfo: Object,
  householdId?: string,
  handleTransfer?: Function,
  transferReasonOptions?: Array<Object>,
  newestLease: Object,
  paymentRestrictions: Object,
  isCommercial: boolean,
  showEditPropertyMoveInDateLink: Function,
  autoEmailMonthlyInvoices: boolean,
  handleAutoEmailMonthlyInvoiceClick: Function,
  monthsWithNSFFees: number,
  applicationId: string,
  organizationId: string,
  propertyId: string,
  promptToaster: Function,
  fasComplete: boolean,
  propertyPaymentProvider: string,
  onCreateFinalAccountStatementReversal: Function,
  householdHasMoveOutProratedTransactions: boolean,
  propertyAutoEmailMonthlyInvoice: boolean,
  isHUDByUnit?: boolean,
  isSection236?: boolean,
  floorPlanAffordablePrograms?: Array<Object>,
  selectedProperty?: Object,
  initialValues?: {
    assignedToId?: string | number,
  },
  assignedTo?: {
    id?: string,
    firstName?: string,
    lastName?: string,
  },
};
const LabelProfileDetails = styled.label.attrs({
  className: 'profile-details',
})``;

// FES-585: inactive users are excluded from the assignees list returned by the
// backend, so a household assigned to a now-inactive user has no matching
// <option> and the browser falls back to the first active user. Re-inject the
// assigned user (with their name from `assignedTo`) so the dropdown keeps
// showing them, but disable that option so they can never be reassigned.
export const buildAssigneeOptions = ({
  users,
  assignedToId,
  assignedTo,
  intl,
}) => {
  if (!assignedToId) {
    return [
      {
        text: intl.formatMessage(messages.unassigned),
        value: '',
        disabled: true,
      },
      ...users,
    ];
  }

  const isAssignedUserListed = users.some(
    (user) => user.value === assignedToId,
  );
  if (isAssignedUserListed || !assignedTo) {
    return users;
  }

  return [
    {
      value: assignedToId,
      text: `${assignedTo.firstName} ${assignedTo.lastName}`,
      disabled: true,
    },
    ...users,
  ];
};

const onManualWriteOffClick = (onSuccess) =>
  confirm(
    'Are you sure you want to write off any open balances on the Resident ledger?',
  ).then(onSuccess);

/**
 * Returns the tooltip message key for the Start Transfer button given the
 * eligibility response and an intl object, or null when the button is enabled.
 * Priority matches the order of checks in the UI (Sc12).
 */
export const buildStartTransferTooltip = (eligibilityData, intl) => {
  const disabledReasons = eligibilityData?.disabledReasons ?? [];
  // A transfer already in progress is the highest-priority block: when one exists none of the
  // other rules matter, so it trumps them all (ENG-30131).
  if (disabledReasons.includes('TRANSFER_IN_PROGRESS')) {
    return intl.formatMessage(messages.startTransferInProgress);
  }
  if (disabledReasons.includes('NO_ACTIVE_UNIT')) {
    return intl.formatMessage(messages.startTransferNoActiveUnit);
  }
  if (disabledReasons.includes('UNDER_EVICTION')) {
    return intl.formatMessage(messages.startTransferUnderEviction);
  }
  if (disabledReasons.includes('DO_NOT_RENEW')) {
    return intl.formatMessage(messages.startTransferDoNotRenew);
  }
  if (disabledReasons.includes('OPEN_RENEWAL_LEASE')) {
    return intl.formatMessage(messages.startTransferOpenRenewal);
  }
  if (disabledReasons.includes('OPEN_CERTIFICATION')) {
    return intl.formatMessage(messages.startTransferOpenCertification);
  }
  return null;
};

/**
 * Computes the Start Transfer button's disabled state and tooltip text together, so the two can't
 * drift apart. Priority (Sc12): missing permission first, then the async eligibility reasons. The
 * in-progress case is handled by hiding the button entirely at render (the Resident Transfer card
 * takes over once a transfer exists), so it is intentionally not a disabled reason here.
 */
export const buildStartTransferButtonState = ({
  canStartTransfer,
  transferEligible,
  eligibilityData,
  intl,
}) => {
  if (!canStartTransfer) {
    return {
      disabled: true,
      tooltip: intl.formatMessage(messages.startTransferNoPermission),
    };
  }
  return {
    disabled: !transferEligible,
    tooltip: buildStartTransferTooltip(eligibilityData, intl) ?? '',
  };
};

const LeaseRenewalStatus = ({ isRenewal, isRenewalComplete }: Object) => {
  if (isRenewal && isRenewalComplete) {
    return 'Completed';
  } else if (isRenewal && not(isRenewalComplete)) {
    return 'Started';
  } else {
    return '---';
  }
};

export const Snapshot = ({
  accountFinalized,
  currentLease,
  doNotRenew,
  fasReady,
  handleAssignedToChange,
  handleDoNotRenewClick,
  handleNoticeToVacateClick,
  handleRenewLease,
  handleUnderEvictionClick,
  hasHouseholdRentableItemEndDateAfterAnticipatedMoveOutDate,
  hasReOpenedRDCertifications,
  hasOpenTransfer, // Might remove
  householdInfo,
  householdId,
  handleTransfer,
  transferReasonOptions,
  intl,
  isResident,
  ledgerBalance,
  manualWriteOff,
  monthsWithLateFees,
  monthsWithNSFFees,
  newestLease,
  onProcessMoveOutClick,
  showEditMoveInDateLink,
  unitHistoryLink,
  showEditMoveOutDateLink,
  underEviction,
  users,
  onPaymentRestrictionChange,
  paymentRestrictions,
  isCommercial,
  showEditPropertyMoveInDateLink,
  handleAutoEmailMonthlyInvoiceClick,
  autoEmailMonthlyInvoices,
  applicationId,
  organizationId,
  propertyId,
  promptToaster,
  fasComplete,
  propertyPaymentProvider,
  residentInfo,
  onCreateFinalAccountStatementReversal,
  isUnitSoftDeleted,
  householdHasMoveOutProratedTransactions,
  initialValues,
  assignedTo,
  propertyAutoEmailMonthlyInvoice,
  isHUDByUnit = false,
  isSection236 = false,
  floorPlanAffordablePrograms = [],
  selectedProperty = {},
}: Props) => {
  if (fasComplete) currentLease.FASDate = moment();
  const { noticeToVacate } = currentLease;
  const anticipatedMoveOutDate = noticeToVacate
    ? noticeToVacate.moveOutDate
    : '';
  const pathArray: Array<any> = ['units', 0, 'unitStatus', 'description'];
  const moveOutDate = !isResident
    ? currentLease.moveOutDate
    : anticipatedMoveOutDate;
  const unitStatus = pathOr('', pathArray, currentLease);
  const isOnNoticeToVacate = unitStatus === 'Occupied / On Notice to Vacate';
  const {
    hudRecertDueDate,
    isLoading: isLoadingHudRecertDueDate,
    isError: hudRecertDueDateFailed,
    refetch: refetchHudRecertDueDate,
  } = useHudRecertDueDate({
    organizationId,
    propertyId,
    householdId,
    isHUDByUnit: isHUDByUnit && isResident,
    isSection236,
    floorPlanAffordablePrograms,
    selectedProperty,
  });
  const hudRecertOverdueBlocksMoveOut = isHudRecertOverdueForMoveOut(
    anticipatedMoveOutDate,
    hudRecertDueDate,
  );
  const moveOutDisabled =
    hasOpenTransfer ||
    unitStatus !== 'Occupied / On Notice to Vacate' ||
    !anticipatedMoveOutDate ||
    hasHouseholdRentableItemEndDateAfterAnticipatedMoveOutDate ||
    hasReOpenedRDCertifications ||
    isLoadingHudRecertDueDate ||
    hudRecertDueDateFailed ||
    hudRecertOverdueBlocksMoveOut;
  //FinancialFixes
  // leaving this here for now until Judy/Kerri decide how to handle manual writeoffs
  const disableManualWriteoffs = true;
  const [mode, setMode] = useState('');
  const unitNumber = currentLease?.units[0]?.number;
  const handleDownloadDocument = useAsyncDownloadFASDocument(
    organizationId,
    propertyId,
    applicationId,
    mode,
    unitNumber,
    promptToaster,
    intl,
  );
  const {
    fasDocumentInExcelFormat,
    paymentsV1,
    reverseFinalAccountStatement: reverseFinalAccountStatementFlag,
    disableFas: disableFasFlag,
    unitTransfers: unitTransfersFlag,
  } = useFlags();

  // Start Transfer eligibility (only fetched when feature flag is ON and component has required IDs)
  const { data: eligibilityData } = useStartTransferEligibility(
    unitTransfersFlag ? organizationId : '',
    unitTransfersFlag ? propertyId : '',
    unitTransfersFlag ? householdId : '',
  );
  const transferEligible = eligibilityData?.enabled ?? false;

  // Whether a transfer is in progress, keyed off the SAME react-query cache the Resident Transfer
  // card uses (activeTransferKey). Using this query — not only the redux hasOpenTransfer flag —
  // keeps the Start Transfer button in sync with the card: it reappears the moment a transfer is
  // canceled (that mutation invalidates this query). While the query is still loading
  // (data === undefined) fall back to the synchronous redux flag so the button doesn't briefly
  // flash for a household that already has a transfer.
  const { data: activeTransfer } = useActiveTransfer(
    organizationId,
    propertyId,
    householdId,
    Boolean(unitTransfersFlag),
  );
  const hasActiveTransfer =
    activeTransfer !== undefined ? Boolean(activeTransfer) : hasOpenTransfer;

  // Retrigger Start Transfer eligibility when an in-page action changes what it
  // depends on: Under Eviction / Do Not Renew toggles (household flags) or a lease
  // renewal creating a renewal lease (current/newest lease changes). Skip the first
  // render so we don't refetch immediately after the query's initial load.
  const queryClient = useQueryClient();
  const eligibilityDepsMounted = useRef(false);
  useEffect(() => {
    if (!eligibilityDepsMounted.current) {
      eligibilityDepsMounted.current = true;
      return;
    }
    queryClient.invalidateQueries([
      'startTransferEligibility',
      organizationId,
      propertyId,
      householdId,
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [underEviction, doNotRenew, currentLease?.id, newestLease?.id]);

  const [startTransferModalOpen, setStartTransferModalOpen] = useState(false);

  const canStartTransfer = useHasPermission('lease-transfer-start');
  // Disabled state + tooltip are computed together (see buildStartTransferButtonState): permission,
  // then the async eligibility reasons. The in-progress case is handled by hiding the button
  // (see the hasOpenTransfer gate on the render below), not by disabling it.
  const { disabled: startTransferDisabled, tooltip: startTransferTooltipText } =
    useMemo(
      () =>
        buildStartTransferButtonState({
          canStartTransfer,
          transferEligible,
          eligibilityData,
          intl,
        }),
      [canStartTransfer, transferEligible, eligibilityData, intl],
    );
  // ENG-31236: one source of truth for "is Start Transfer on screen" — it drives both the render gate
  // below and the flex layout that sizes it identically to Process Move-Out, so the two cannot
  // disagree (flag off, or an active transfer, means no Start Transfer and therefore no stretching).
  const showStartTransfer = unitTransfersFlag && !hasActiveTransfer;
  const { stackSx, tooltipContainerSx, buttonWrapperSx, fullWidth } =
    buildTransferActionLayout(showStartTransfer);
  // Process Move Out requires the 'resident-move-out' scope. Fold the permission check into the
  // button's disabled state (previously enforced by the ElementWithPermissions wrapper that the
  // tooltip refactor replaced) so users without the scope can't trigger the flow.
  const canProcessMoveOut = useHasPermission('resident-move-out');
  // Process Move Out is disabled for several reasons; surface a tooltip (matching the Start Transfer
  // button's pattern/voice) so the user knows why. Priority: missing permission first, then an
  // in-progress transfer, then the HUD recertification due date (loading, failed, overdue).
  const moveOutTooltipText = !canProcessMoveOut
    ? intl.formatMessage(messages.processMoveOutNoPermission)
    : hasOpenTransfer
    ? intl.formatMessage(messages.processMoveOutInProgressTransfer)
    : isLoadingHudRecertDueDate
    ? intl.formatMessage(messages.processMoveOutHudRecertLoading)
    : hudRecertDueDateFailed
    ? intl.formatMessage(messages.processMoveOutHudRecertLoadError)
    : hudRecertOverdueBlocksMoveOut
    ? intl.formatMessage(messages.processMoveOutHudRecertOverdue)
    : '';
  const { propertyConfig, isLoading } = useFetchPropertyConfig({
    organizationId,
    propertyId,
    flags: { paymentsV1 },
  });
  const shouldAcceptPartialPayment =
    !isLoading && propertyConfig[0]?.shouldAcceptPartialPayment;

  const { data: finalAccountStatement } =
    useGetFinalAccountStatement(residentInfo);

  const {
    data: finalAccountStatementReversal,
    setData: setFinalAccountStatementReversal,
  } = useGetFinalAccountStatementReversal(finalAccountStatement);

  const { open, openModal, closeModal } = useModal();

  const [isModalConfirmLoading, setIsModalConfirmLoading] =
    React.useState(false);

  const reverseFas = React.useCallback(() => {
    const makeRequest = async () => {
      setIsModalConfirmLoading(true);
      setFinalAccountStatementReversal({});
      const finalAccountStatementReversalService =
        new FinalAccountStatementReversalService();
      try {
        const result = await finalAccountStatementReversalService.create({
          finalAccountStatementId: finalAccountStatement.id,
        });
        setIsModalConfirmLoading(false);
        closeModal();
        toastr.info(
          intl.formatMessage(
            messages.reverseFinalAccountStatementRequestedToastrTitle,
          ),
          intl.formatMessage(
            messages.reverseFinalAccountStatementRequestedToastrMessage,
          ),
        );
        setFinalAccountStatementReversal(result);
        onCreateFinalAccountStatementReversal &&
          onCreateFinalAccountStatementReversal(result);
        return result;
      } catch (error) {
        setIsModalConfirmLoading(false);
        toastr.error('Error', error?.message || error?.networkError || error);
        setFinalAccountStatementReversal(null);
        return null;
      }
    };
    makeRequest();
  }, [
    setIsModalConfirmLoading,
    finalAccountStatement?.id,
    closeModal,
    setFinalAccountStatementReversal,
    onCreateFinalAccountStatementReversal,
    intl,
  ]);

  const onReverseFinalAccountStatementClicked = React.useCallback(() => {
    openModal();
  }, [openModal]);

  const assignedToId = initialValues?.assignedToId;

  const assigneeOptions = useMemo(
    () => buildAssigneeOptions({ users, assignedToId, assignedTo, intl }),
    [assignedToId, assignedTo, users, intl],
  );

  return (
    <>
      <Box padding={2}>
        <Stack spacing={1} marginBottom={2}>
          <IconTitle
            icon={<i key="1" className="et-prospect" />}
            message={messages.snapshot}
          />
          {householdHasMoveOutProratedTransactions ? (
            <MoveOutProrateBanner intl={intl} />
          ) : null}
          {isResident ? (
            <Stack direction={'row'} spacing={2}>
              <span>
                <div className="custom-checkbox" style={{ marginBottom: 0 }}>
                  <label>
                    <ElementWithPermissions scope={['resident-edit']}>
                      <input
                        type="checkbox"
                        checked={underEviction}
                        onChange={handleUnderEvictionClick}
                        disabled={!isResident || hasOpenTransfer}
                      />
                    </ElementWithPermissions>
                    <span className="custom-check-square" />
                    <FormattedMessage {...messages.underEviction} />
                  </label>
                </div>
              </span>
              <span>
                <div className="custom-checkbox" style={{ marginBottom: 0 }}>
                  <label style={{ whiteSpace: 'nowrap' }}>
                    <ElementWithPermissions scope={['resident-edit']}>
                      <input
                        type="checkbox"
                        checked={doNotRenew}
                        onChange={handleDoNotRenewClick}
                        disabled={!isResident || hasOpenTransfer}
                      />
                    </ElementWithPermissions>
                    <span className="custom-check-square" />
                    <FormattedMessage {...messages.doNotRenew} />
                  </label>
                </div>
              </span>
            </Stack>
          ) : null}

          <Stack direction={'row'} spacing={2}>
            <span>
              <div className="custom-checkbox" style={{ marginBottom: 0 }}>
                <label>
                  <input
                    type="checkbox"
                    checked={paymentRestrictions.certifiedOnly}
                    disabled={true}
                  />

                  <span className="custom-check-square" />
                  <FormattedMessage {...messages.certifiedOnly} />
                </label>
              </div>
            </span>
            <span>
              <div className="custom-checkbox" style={{ marginBottom: 0 }}>
                <label>
                  <input
                    type="checkbox"
                    checked={paymentRestrictions.doNotAccept}
                    disabled={true}
                  />
                  <span className="custom-check-square" />
                  <FormattedMessage {...messages.doNotAccept} />
                </label>
              </div>
            </span>
          </Stack>
          <Stack spacing={2} direction={'row'}>
            {shouldAcceptPartialPayment &&
              propertyPaymentProvider === 'FORTRESS' &&
              paymentsV1 && (
                <span>
                  <div className="custom-checkbox" style={{ marginBottom: 0 }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={paymentRestrictions.doNotAcceptPartialPayments}
                        disabled={true}
                      />

                      <span className="custom-check-square" />
                      <FormattedMessage
                        {...messages.doNotAcceptPartialPayments}
                      />
                    </label>
                  </div>
                </span>
              )}
            {isResident && isCommercial ? (
              <span>
                <div
                  className="custom-checkbox"
                  style={{
                    marginBottom: 0,
                    display: 'flex',
                    spacing: 1,
                  }}
                >
                  <label>
                    <ElementWithPermissions scope={['auto-monthly-invoice']}>
                      <input
                        type="checkbox"
                        checked={
                          propertyAutoEmailMonthlyInvoice
                            ? autoEmailMonthlyInvoices
                            : false
                        }
                        onChange={handleAutoEmailMonthlyInvoiceClick}
                        disabled={
                          !isResident || !propertyAutoEmailMonthlyInvoice
                        }
                      />
                    </ElementWithPermissions>

                    <span className="custom-check-square" />
                    <FormattedMessage {...messages.autoEmailMonthlyInvoice} />
                  </label>
                  {!propertyAutoEmailMonthlyInvoice && (
                    <Tooltip
                      placement="right"
                      title={intl.formatMessage({
                        ...messages.autoEmailMonthlyInvoiceTooltip,
                      })}
                    >
                      <ListItemIcon sx={{ marginLeft: '5px' }}>
                        <AlertInfoIcon fontSize="small" sx={{ mt: 0.5 }} />
                      </ListItemIcon>
                    </Tooltip>
                  )}
                </div>
              </span>
            ) : (
              ''
            )}
          </Stack>
        </Stack>
        <Row className="row">
          <Col xs={12} md={3}>
            <LabelProfileDetails>
              <FormattedMessage {...messages.assignedTo} />
            </LabelProfileDetails>
          </Col>
          <Col xs={12} md={8}>
            <ElementWithPermissions
              scope={['prospect-assign', 'prospect-update']}
            >
              <select
                id="assignedToId"
                name="assignedToId"
                value={assignedToId ?? ''}
                onChange={(event) =>
                  handleAssignedToChange(event, event.target.value)
                }
              >
                {assigneeOptions.map((option) => (
                  <option
                    key={option.value || 'unassigned'}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.text}
                  </option>
                ))}
              </select>
            </ElementWithPermissions>
          </Col>
        </Row>
        <Divider />
        <DetailList>
          {isResident && (
            <li>
              <span>
                <FormattedMessage {...messages.unitStatus} />
              </span>{' '}
              {unitStatus}
              <ElementWithPermissions scope={['resident-edit']}>
                <a
                  className="float-right"
                  onClick={() => handleNoticeToVacateClick()}
                >
                  <strong>
                    <FormattedMessage {...messages.noticeToVacate} />
                  </strong>
                </a>
              </ElementWithPermissions>
            </li>
          )}
          {!isResident && (
            <li>
              <ElementWithPermissions scope={['resident-edit']}>
                <a
                  className="float-right"
                  onClick={() => handleNoticeToVacateClick()}
                >
                  <strong>
                    <FormattedMessage {...messages.noticeToVacate} />
                  </strong>
                </a>
              </ElementWithPermissions>
            </li>
          )}
          <li style={{ paddingTop: !isResident ? '25px' : '0px' }}>
            <span>
              <FormattedMessage {...messages.currentLedgerBalance} />
            </span>{' '}
            {intl.formatNumber(ledgerBalance, {
              style: 'currency',
              currency: 'USD',
            })}
            {!isResident && !disableManualWriteoffs && (
              <span className="text-right assign-btns">
                <ElementWithPermissions scope={['write-off']}>
                  <a
                    onClick={() => onManualWriteOffClick(manualWriteOff)}
                    disabled={!accountFinalized}
                  >
                    Write Off
                  </a>
                </ElementWithPermissions>
              </span>
            )}
          </li>
          <li>
            <span>
              <FormattedMessage {...messages.numberOfLateFees} />
            </span>{' '}
            {monthsWithLateFees}
          </li>
          <li>
            <span>
              <FormattedMessage {...messages.numberOfNSFFees} />
            </span>{' '}
            {monthsWithNSFFees}
          </li>
          <li>
            <span>Lease Start Date: </span>{' '}
            <FormattedDateOrDashes value={currentLease.startDate} />
          </li>
          <li>
            <span>
              <FormattedMessage {...messages.leaseEndDate} />
            </span>{' '}
            <FormattedDateOrDashes value={currentLease.endDate} />
          </li>
          <li />
          <li>
            <span>
              <FormattedMessage {...messages.moveInDate} />
            </span>{' '}
            <FormattedDateOrDashes value={currentLease.actualMoveInDate} />{' '}
            {showEditMoveInDateLink()}
          </li>
          {isCommercial && (
            <li>
              <span>
                <FormattedMessage {...messages.rentStartDate} />
              </span>{' '}
              <FormattedDateOrDashes value={currentLease.rentStartDate} />
            </li>
          )}
          <li>
            <span>
              <FormattedMessage {...messages.propertyMoveInDate} />
            </span>{' '}
            <FormattedDateOrDashes value={householdInfo.householdMoveInDate} />{' '}
            {showEditPropertyMoveInDateLink()}
          </li>
          {unitHistoryLink && <li>{unitHistoryLink}</li>}
          <li>
            <span>
              {isResident ? (
                <span>
                  <FormattedMessage {...messages.anticipated} />{' '}
                </span>
              ) : null}
              <FormattedMessage {...messages.moveOutDate} />
            </span>{' '}
            <FormattedDateOrDashes value={moveOutDate} />{' '}
            {showEditMoveOutDateLink()}
          </li>
          {isResident && (
            <li>
              <span>
                <FormattedMessage {...messages.leaseRenewalStatus} />
              </span>{' '}
              {LeaseRenewalStatus(newestLease)}
              {!isCommercial && (
                <ElementWithPermissions scope={['lease-read', 'lease-create']}>
                  <a className="float-right" onClick={handleRenewLease}>
                    <strong>
                      <FormattedMessage {...messages.quoteRenewal} />
                    </strong>
                  </a>
                </ElementWithPermissions>
              )}
            </li>
          )}
        </DetailList>
        {isResident ? (
          <Stack spacing={1} alignItems={'center'}>
            <Stack
              direction="row"
              spacing={TRANSFER_ACTION_ROW_SPACING}
              justifyContent="center"
              alignItems="center"
              sx={stackSx}
            >
              {/* Hide Start Transfer entirely while a transfer is in progress: the Resident
                  Transfer card takes over the transfer UI, so offering Start Transfer here would
                  be redundant/confusing. hasActiveTransfer is driven by the same activeTransfer
                  query as the card, so it reappears the moment a transfer is canceled. */}
              {showStartTransfer && (
                <Tooltip
                  title={startTransferTooltipText}
                  placement="top"
                  variant="light"
                  containerSX={tooltipContainerSx}
                >
                  {/* span wrapper: a disabled MUI button swallows pointer
                      events, so the Tooltip needs a non-disabled wrapper to
                      fire on hover/focus. It is also the flex child that gives
                      this button the same width as Process Move-Out. */}
                  <Box component="span" sx={buttonWrapperSx}>
                    <Button
                      variant={'primarySubtle'}
                      type="button"
                      fullWidth={fullWidth}
                      disabled={startTransferDisabled}
                      onClick={() => setStartTransferModalOpen(true)}
                    >
                      <FormattedMessage {...messages.startTransfer} />
                    </Button>
                  </Box>
                </Tooltip>
              )}
              <Tooltip
                title={moveOutTooltipText}
                placement="top"
                variant="light"
                containerSX={tooltipContainerSx}
              >
                {/* span wrapper: a disabled MUI button swallows pointer events,
                      so the Tooltip needs a non-disabled wrapper to fire on
                      hover/focus. Empty title renders no tooltip. */}
                <Box component="span" sx={buttonWrapperSx}>
                  <Button
                    variant={'primary'}
                    type="button"
                    fullWidth={fullWidth}
                    onClick={onProcessMoveOutClick}
                    disabled={moveOutDisabled || !canProcessMoveOut}
                  >
                    <FormattedMessage {...messages.processMoveOut} />
                  </Button>
                </Box>
              </Tooltip>
            </Stack>
            {hasHouseholdRentableItemEndDateAfterAnticipatedMoveOutDate ||
            (isOnNoticeToVacate && hasReOpenedRDCertifications) ? (
              <Typography variant={'bodySmall'} align={'center'} maxWidth={450}>
                {hasHouseholdRentableItemEndDateAfterAnticipatedMoveOutDate ? (
                  <FormattedMessage
                    {...messages.disableMoveOutHouseholdRentableItemsAfterMoveOutDate}
                  />
                ) : null}
                {isOnNoticeToVacate && hasReOpenedRDCertifications ? (
                  <FormattedMessage
                    {...messages.disableMoveOutOpenReOpenedRDCertifications}
                  />
                ) : null}
              </Typography>
            ) : null}
            {hudRecertDueDateFailed ? (
              <Typography variant={'bodySmall'} align={'center'} maxWidth={450}>
                <FormattedMessage
                  {...messages.processMoveOutHudRecertLoadError}
                />{' '}
                <Button
                  variant={'text'}
                  type="button"
                  onClick={() => refetchHudRecertDueDate()}
                >
                  <FormattedMessage {...messages.retry} />
                </Button>
              </Typography>
            ) : null}
          </Stack>
        ) : (
          <div className="row">
            {fasDocumentInExcelFormat && accountFinalized && (
              <div className="row">
                <div className="col-xs-6">
                  <Button
                    onClick={() => {
                      setMode('xlsx');
                      handleDownloadDocument();
                    }}
                    variant="primarySubtle"
                    sx={{
                      width: '140px',
                      float: 'right',
                      '&:hover': {
                        '.xlsx-xPath2': {
                          fill: '#4D73FF',
                        },
                        '.xlsx-xPath1': {
                          fill: 'white',
                        },
                      },
                      '.xlsx-xPath1': {
                        fill: '#4D73FF',
                      },
                      '.xlsx-xPath2': {
                        fill: 'white',
                      },
                    }}
                  >
                    <span className="padright5">
                      <XlsxIcon />
                    </span>
                    <FormattedMessage {...messages.fasExcel} />
                  </Button>
                </div>
                <div className="col-xs-6">
                  <Button
                    onClick={() => {
                      setMode('pdf');
                      handleDownloadDocument();
                    }}
                    variant="primarySubtle"
                    sx={{
                      width: '140px',
                      '&:hover': {
                        '.pdf-icon': {
                          fill: 'white',
                        },
                      },
                      '.pdf-icon': {
                        fill: '#4D73FF',
                      },
                    }}
                  >
                    <span className="padright5">
                      <PdfIcon />
                    </span>
                    <FormattedMessage {...messages.fasPdf} />
                  </Button>
                </div>
              </div>
            )}
            <div className="row">
              <div
                className="col-xs-12 text-center"
                style={{ display: 'grid' }}
              >
                <ElementWithPermissions
                  scope={['final-account-statement-create']}
                >
                  <button
                    color="green"
                    disabled={
                      !fasReady ||
                      !!accountFinalized ||
                      currentLease.FASDate ||
                      isUnitSoftDeleted ||
                      disableFasFlag
                    }
                    className="btn btn-shout"
                    onClick={() => {
                      handleNoticeToVacateClick(true);
                    }}
                  >
                    <span>
                      <FormattedMessage {...messages.finalAccountStatement} />
                    </span>
                  </button>
                </ElementWithPermissions>
                {!isNil(currentLease.FASDate) &&
                  reverseFinalAccountStatementFlag && (
                    <ElementWithPermissions
                      scope={['final-account-statement-reverse']}
                    >
                      <Tooltip
                        title={
                          finalAccountStatementReversal &&
                          !finalAccountStatementReversal.completedAt
                            ? intl.formatMessage(
                                messages.reverseFinalAccountStatementInProgressTooltip,
                              )
                            : ''
                        }
                        style={{ width: '100%' }}
                      >
                        <span>
                          <button
                            className="btn btn-tertiary"
                            onClick={() =>
                              onReverseFinalAccountStatementClicked()
                            }
                            style={{ marginTop: '0px', width: '100%' }}
                            disabled={
                              !finalAccountStatement?.id ||
                              (finalAccountStatementReversal &&
                                !finalAccountStatementReversal.completedAt) ||
                              isUnitSoftDeleted
                            }
                          >
                            <span>
                              <FormattedMessage
                                {...messages.reverseFinalAccountStatementButton}
                              />
                            </span>
                          </button>
                        </span>
                      </Tooltip>
                      {!finalAccountStatement?.id ? (
                        <h2 className="col-xs-12 text-center no-margin">
                          <small className="text-gray--darker">
                            <FormattedMessage
                              {...messages.reverseFinalAccountStatementNotCompletedInSystem}
                            />
                          </small>
                        </h2>
                      ) : null}
                    </ElementWithPermissions>
                  )}
              </div>
              {!fasReady ? (
                <h2 className="col-xs-12 text-center">
                  <small className="text-gray">
                    <FormattedMessage
                      {...messages.finalAccountStatementDisabled}
                    />
                  </small>
                </h2>
              ) : null}
              {accountFinalized ? (
                <h2 className="col-xs-12 text-center">
                  <small className="text-center text-gray--darker">
                    <FormattedMessage
                      {...messages.finalAccountStatementAlreadyGenerated}
                    />{' '}
                    on {moment(currentLease.FASDate).format('MM/DD/YYYY')}.
                  </small>
                </h2>
              ) : null}
            </div>
          </div>
        )}
      </Box>

      <Modal
        open={open}
        onClose={closeModal}
        title={
          <>
            <AlertCriticalIcon sx={{ color: red.dark }} />
            <Typography mr={1} variant="titleMedium" sx={{ color: red.dark }}>
              <FormattedMessage
                {...messages.reverseFinalAccountStatementModalTitle1}
              />
            </Typography>
            <Typography variant="titleMedium">
              <FormattedMessage
                {...messages.reverseFinalAccountStatementModalTitle2}
              />
            </Typography>
          </>
        }
        actionsProps={[
          {
            children: 'Cancel',
            onClick: () => {
              closeModal();
            },
          },
          {
            children: 'Confirm',
            submitButton: true,
            isSubmitting: isModalConfirmLoading,
            disabled: !finalAccountStatement?.id || isModalConfirmLoading,
            onClick: async () => reverseFas(),
          },
        ]}
        ModalProps={{
          sx: {
            '.MuiPaper-root': {
              maxWidth: '428px',
            },
          },
        }}
      >
        <p>
          <FormattedMessage
            {...messages.reverseFinalAccountStatementModalLine1}
          />{' '}
          {moment(currentLease.FASDate).format('YYYY-MM-DD')}.
        </p>
        <p>
          <b>
            <FormattedMessage
              {...messages.reverseFinalAccountStatementModalLine2}
            />
          </b>
        </p>
        <ul>
          <li>
            <FormattedMessage
              {...messages.reverseFinalAccountStatementModalLine3}
            />
          </li>
          <li>
            <FormattedMessage
              {...messages.reverseFinalAccountStatementModalLine4}
            />
          </li>
        </ul>
        <p>
          <b>
            <FormattedMessage
              {...messages.reverseFinalAccountStatementModalLine5}
            />
          </b>
        </p>
        <p>
          <FormattedMessage
            {...messages.reverseFinalAccountStatementModalLine6}
          />
        </p>
      </Modal>
      {unitTransfersFlag && (
        <StartTransferModal
          open={startTransferModalOpen}
          onClose={() => setStartTransferModalOpen(false)}
          organizationId={organizationId}
          propertyId={propertyId}
          householdId={householdId ?? ''}
          transferReasonOptions={transferReasonOptions ?? []}
          // ENG-31072: transfer certifications are only created for the affordable programs on
          // the resident's current unit floorplan, so conventional households must not be told
          // certifications will be created.
          hasAffordablePrograms={
            eligibilityData?.hasAffordablePrograms !== false
          }
          onConfirm={() => {
            if (typeof handleTransfer === 'function') {
              handleTransfer();
            }
          }}
        />
      )}
    </>
  );
};

export default injectIntl(Snapshot);
